import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAccountPreferences, useUpdateAccountPreferences } from "@/queries/account";
import { toast } from "sonner";
import { FastForward } from "lucide-react";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

export default function HeatFlowSettingCard() {
  const { data, isLoading } = useAccountPreferences();
  const updatePreferences = useUpdateAccountPreferences();
  const [autoScrollNextHeat, setAutoScrollNextHeat] = useState(
    () => data?.preferences?.auto_scroll_next_heat !== false
  );

  useEffect(() => {
    setAutoScrollNextHeat(data?.preferences?.auto_scroll_next_heat !== false);
  }, [data]);

  if (isLoading && getNativePlatform() === "android") {
    return <NativeSettingsSkeleton sections={1} />;
  }

  const toggleAutoScrollNextHeat = async (checked) => {
    if (checked === autoScrollNextHeat || updatePreferences.isPending) return;

    setAutoScrollNextHeat(checked);
    try {
      await toast.promise(
        updatePreferences.mutateAsync({
          preferences: {
            auto_scroll_next_heat: checked,
            allow_haptics: data?.preferences?.allow_haptics !== false,
            haptic_intensity: data?.preferences?.haptic_intensity || "normal",
            protocol_default_view: data?.preferences?.protocol_default_view || "cards",
            protocol_default_view_confirmed:
              data?.preferences?.protocol_default_view_confirmed || false,
          },
        }),
        {
          loading: "Sparar inställning...",
          success: checked
            ? "Automatisk scroll till nästa heat aktiverad"
            : "Automatisk scroll till nästa heat avstängd",
          error: (error) => error?.message || "Kunde inte spara inställningen",
        }
      );
    } catch {
      setAutoScrollNextHeat(!checked);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FastForward className="h-5 w-5 shrink-0 opacity-70" />
          <span>Protokollflöde</span>
        </CardTitle>
        <CardDescription>
          Styr hur protokollet beter sig när du registrerar flera heat i följd.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-border bg-muted/20 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div className="font-medium">Automatiskt scrolla till nästa heat</div>
              <p className="text-sm text-muted-foreground">
                Flytta heatöversikten till nästa ofärdiga heat efter att du sparat ett heat.
              </p>
            </div>
            <Switch
              checked={autoScrollNextHeat}
              disabled={isLoading || updatePreferences.isPending}
              onCheckedChange={toggleAutoScrollNextHeat}
              aria-label="Automatiskt scrolla till nästa heat"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
